import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import axios from "../api/baseurl";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import "./hm-theme.css";

const MapCompanyAndLabs = ({ isOpen, onRequestClose, companyId }) => {
  const [labs, setLabs] = useState([]);
  const [selectedLabs, setSelectedLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mappedLabs, setMappedLabs] = useState([]);
  const [filteredLabs, setFilteredLabs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const token = JSON.parse(localStorage.getItem("data"))?.token;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLabsAndMappedLabs = async () => {
      setLoading(true);
      try {
        const compRes = await axios.get(`/api/companydetails/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const labsResponse = await axios.get(`/api/labdetails/regulated=true/adminlink=${compRes.data?.adminlink}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const mappedLabsResponse = await axios.get(
          `/api/company-labs-mapping/companyId=${companyId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const availableLabs = labsResponse.data || [];
        const alreadyMappedIds = (mappedLabsResponse.data || []).map((mappedLab) => mappedLab.labId);

        setLabs(availableLabs);
        setMappedLabs(alreadyMappedIds);
        setFilteredLabs(availableLabs.filter((lab) => !alreadyMappedIds.includes(lab.id)));
        setLoading(false);
      } catch (error) {
        setLoading(false);
        handleError(error);
      }
    };

    if (companyId) {
      fetchLabsAndMappedLabs();
    }
  }, [companyId]);

  function handleError(error) {
    if (error.response?.status === 401) {
      navigate(`/signin`);
      toast.error("Your session has expired. Please login.");
    } else {
      toast.error(error.response?.data?.message || error.message || "An error occurred.");
    }
  }

  const handleLabsSelection = (labId) => {
    setSelectedLabs((prevSelected) =>
      prevSelected.includes(labId)
        ? prevSelected.filter((id) => id !== labId)
        : [...prevSelected, labId]
    );
  };

  const handleSaveMapping = async (e) => {
    e.preventDefault();
    if (selectedLabs.length === 0) {
      toast.error("Please select at least one lab to link");
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `/api/company-labs-mapping/create`,
        selectedLabs.map((labId) => ({
          companyId: companyId,
          labId: labId,
        })),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Labs mapped successfully!");
      setLoading(false);
      onRequestClose();
    } catch (error) {
      handleError(error);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = labs
      .filter((lab) => !mappedLabs.includes(lab.id))
      .filter(
        (lab) =>
          lab.labname?.toLowerCase().includes(term) ||
          lab.labcode?.toLowerCase().includes(term)
      );
    setFilteredLabs(filtered);
  };

  return (
    <Modal show={isOpen} onHide={onRequestClose} centered size="lg" className="hm-modal">
      <Modal.Header closeButton className="hm-card-header">
        <Modal.Title className="hm-card-title">
          <i className="material-icons">biotech</i>
          Link Labs to Company
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {loading && <div className="loading"></div>}
        <Form onSubmit={handleSaveMapping}>
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold small text-secondary mb-2">Available Labs</Form.Label>
            
            {/* Search Input */}
            <div className="position-relative mb-3">
              <input
                type="search"
                className="form-control ps-4"
                placeholder="Search by Lab name or Lab code..."
                value={searchTerm}
                onChange={handleSearch}
              />
              <i className="material-icons position-absolute text-muted" style={{ left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "1.1rem" }}>
                search
              </i>
            </div>

            {/* Labs Selection Scroll Box */}
            <div
              className="p-3 border rounded-3 bg-light"
              style={{ maxHeight: "320px", overflowY: "auto" }}
            >
              {filteredLabs.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="material-icons d-block mb-1" style={{ fontSize: "2rem" }}>science_off</i>
                  <span className="small">No available labs found to link</span>
                </div>
              ) : (
                <Row className="g-2">
                  {filteredLabs.map((lab) => {
                    const isSelected = selectedLabs.includes(lab.id);
                    return (
                      <Col key={lab.id} md={6}>
                        <div
                          className={`p-3 border rounded-3 bg-white h-100 transition-all cursor-pointer d-flex align-items-center justify-content-between ${
                            isSelected ? "border-primary shadow-sm bg-primary bg-opacity-10" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleLabsSelection(lab.id)}
                        >
                          <div className="d-flex align-items-center gap-2 overflow-hidden">
                            <Form.Check
                              type="checkbox"
                              id={`lab-chk-${lab.id}`}
                              checked={isSelected}
                              onChange={() => {}}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLabsSelection(lab.id);
                              }}
                            />
                            <div className="overflow-hidden">
                              <h6 className="fw-semibold mb-0 text-truncate text-dark small">{lab.labname}</h6>
                              <span className="hm-badge hm-badge-primary extra-small mt-1">{lab.labcode}</span>
                            </div>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </div>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 pt-2 border-top">
            <Button variant="secondary" className="rounded-pill px-4" onClick={onRequestClose} disabled={loading}>
              Cancel
            </Button>
            <button type="submit" className="btn-hm-primary" disabled={loading || selectedLabs.length === 0}>
              <i className="material-icons" style={{ fontSize: "1rem" }}>link</i>
              Save Mapping ({selectedLabs.length})
            </button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default MapCompanyAndLabs;

